import React, { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { projectsApi } from '../../api/client';
import { Project, ProjectStatus, Department } from '../../types';
import { DeptBadge } from '../../components/ui/DeptBadge';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { ProgressBar } from '../../components/ui/ProgressBar';
import {
  Plus, Search, Grid3X3, List, Clock, AlertTriangle, ChevronRight,
} from 'lucide-react';

// ─── Mock fallback data ───────────────────────────────────────────────────────

const MOCK: Project[] = [
  {
    id: 1, reference: 'PRJ-001', title: 'Camisetas DTF Temporada Verano',
    status: 'pending', currentDepartment: 'dtf',
    notifications: { email: true, onStateChange: true },
    client: { id: 1, name: 'Moda Express', company: 'Moda Express SL' },
    dueDate: new Date(Date.now() + 2 * 86400000).toISOString(),
    createdAt: new Date().toISOString(), progress: 0,
    steps: [
      { id: 1, department: 'gestor',   orderIndex: 0, status: 'done',    data: {} },
      { id: 2, department: 'dtf',      orderIndex: 1, status: 'pending', data: {} },
      { id: 3, department: 'embalaje', orderIndex: 2, status: 'pending', data: {} },
    ],
  },
  {
    id: 2, reference: 'PRJ-002', title: 'Flyers Corporativos Q3',
    status: 'in_progress', currentDepartment: 'imprenta',
    notifications: { email: true, onStateChange: false },
    client: { id: 2, name: 'Tech Startup SL' },
    dueDate: new Date(Date.now() - 86400000).toISOString(),
    createdAt: new Date().toISOString(), progress: 45,
    steps: [
      { id: 4, department: 'gestor',    orderIndex: 0, status: 'done',        data: {} },
      { id: 5, department: 'financiero',orderIndex: 1, status: 'done',        data: {} },
      { id: 6, department: 'imprenta',  orderIndex: 2, status: 'in_progress', data: {} },
      { id: 7, department: 'embalaje',  orderIndex: 3, status: 'pending',     data: {} },
    ],
  },
  {
    id: 3, reference: 'PRJ-003', title: 'Roll-ups Feria Internacional',
    status: 'in_progress', currentDepartment: 'gestor',
    notifications: { email: false, onStateChange: true },
    client: { id: 3, name: 'Expo & Trade' },
    dueDate: new Date(Date.now() + 7 * 86400000).toISOString(),
    createdAt: new Date().toISOString(), progress: 60,
    steps: [
      { id: 8, department: 'gestor',   orderIndex: 0, status: 'in_progress', data: {} },
      { id: 9, department: 'imprenta', orderIndex: 1, status: 'pending',     data: {} },
    ],
  },
  {
    id: 4, reference: 'PRJ-004', title: 'Bolsas personalizadas boda',
    status: 'done', currentDepartment: 'embalaje',
    notifications: { email: true, onStateChange: true },
    client: { id: 4, name: 'Eventos Premium' },
    dueDate: new Date(Date.now() - 3 * 86400000).toISOString(),
    completedAt: new Date().toISOString(),
    createdAt: new Date().toISOString(), progress: 100,
    steps: [
      { id: 10, department: 'gestor',   orderIndex: 0, status: 'done', data: {} },
      { id: 11, department: 'almacen',  orderIndex: 1, status: 'done', data: {} },
      { id: 12, department: 'embalaje', orderIndex: 2, status: 'done', data: {} },
    ],
  },
  {
    id: 5, reference: 'PRJ-005', title: 'Tarjetas de visita Premium',
    status: 'pending', currentDepartment: 'financiero',
    notifications: { email: false, onStateChange: false },
    client: { id: 5, name: 'Consultoría Norte' },
    dueDate: new Date(Date.now() + 5 * 86400000).toISOString(),
    createdAt: new Date().toISOString(), progress: 0,
    steps: [
      { id: 13, department: 'gestor',     orderIndex: 0, status: 'pending', data: {} },
      { id: 14, department: 'financiero', orderIndex: 1, status: 'pending', data: {} },
      { id: 15, department: 'imprenta',   orderIndex: 2, status: 'pending', data: {} },
    ],
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
}

function isOverdue(iso: string) {
  return new Date(iso).getTime() < Date.now();
}

const STATUS_LABELS: Record<ProjectStatus, string> = {
  pending: 'Pendiente',
  in_progress: 'En proceso',
  done: 'Completado',
  cancelled: 'Cancelado',
};

const DEPT_OPTIONS: Department[] = ['gestor', 'financiero', 'imprenta', 'dtf', 'almacen', 'embalaje'];

// ─── Grid Card ────────────────────────────────────────────────────────────────

function ProjectGridCard({ project, onClick, isMobile }: { project: Project; onClick: () => void; isMobile: boolean }) {
  const departments = [...new Set(project.steps.map(s => s.department))];
  const overdue = project.dueDate && isOverdue(project.dueDate) && project.status !== 'done';

  return (
    <div
      onClick={onClick}
      style={{
        background: '#12121a',
        border: '1px solid #1e1e2e',
        borderRadius: 14,
        padding: isMobile ? '14px 16px' : '18px 20px',
        cursor: 'pointer',
        transition: 'all 0.15s',
      }}
      onMouseEnter={e => {
        (e.currentTarget as HTMLDivElement).style.border = '1px solid #2e2e42';
        (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-2px)';
        (e.currentTarget as HTMLDivElement).style.boxShadow = '0 8px 24px rgba(0,0,0,0.3)';
      }}
      onMouseLeave={e => {
        (e.currentTarget as HTMLDivElement).style.border = '1px solid #1e1e2e';
        (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)';
        (e.currentTarget as HTMLDivElement).style.boxShadow = 'none';
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
        <span style={{ fontSize: 10, color: '#4a4858', fontFamily: 'JetBrains Mono, monospace' }}>{project.reference}</span>
        <StatusBadge status={project.status} />
      </div>
      <div style={{ fontSize: isMobile ? 13 : 14, fontWeight: 600, color: '#e2e0f0', marginBottom: 6, lineHeight: 1.3 }}>{project.title}</div>
      {project.client && (
        <div style={{ fontSize: 12, color: '#6b6880', marginBottom: 12 }}>{project.client.company || project.client.name}</div>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 12 }}>
        {departments.map(d => <DeptBadge key={d} dept={d} size="xs" />)}
      </div>
      <ProgressBar value={project.progress} height={3} />
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12, alignItems: 'center' }}>
        {project.dueDate && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {overdue ? <AlertTriangle size={10} color="#ef4444" /> : <Clock size={10} color="#6b6880" />}
            <span style={{ fontSize: 10, color: overdue ? '#ef4444' : '#6b6880', fontFamily: 'JetBrains Mono, monospace' }}>{formatDate(project.dueDate)}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Table Row ────────────────────────────────────────────────────────────────

function ProjectTableRow({ project, onClick }: { project: Project; onClick: () => void }) {
  const departments = [...new Set(project.steps.map(s => s.department))];
  const overdue = project.dueDate && isOverdue(project.dueDate) && project.status !== 'done';

  return (
    <tr
      onClick={onClick}
      style={{ cursor: 'pointer', transition: 'background 0.1s' }}
      onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.02)')}
      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
    >
      <td style={{ padding: '12px 16px', borderBottom: '1px solid #1a1a28' }}>
        <div style={{ fontSize: 10, color: '#4a4858', fontFamily: 'JetBrains Mono, monospace', marginBottom: 3 }}>{project.reference}</div>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e0f0' }}>{project.title}</div>
        {project.client && <div style={{ fontSize: 11, color: '#6b6880', marginTop: 2 }}>{project.client.company || project.client.name}</div>}
      </td>
      <td style={{ padding: '12px 16px', borderBottom: '1px solid #1a1a28' }}>
        <StatusBadge status={project.status} />
      </td>
      <td style={{ padding: '12px 16px', borderBottom: '1px solid #1a1a28' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {departments.slice(0, 3).map(d => <DeptBadge key={d} dept={d} size="xs" />)}
          {departments.length > 3 && <span style={{ fontSize: 10, color: '#4a4858' }}>+{departments.length - 3}</span>}
        </div>
      </td>
      <td style={{ padding: '12px 16px', borderBottom: '1px solid #1a1a28', minWidth: 120 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ProgressBar value={project.progress} height={4} />
          <span style={{ fontSize: 11, color: '#f97316', fontFamily: 'JetBrains Mono, monospace', whiteSpace: 'nowrap' }}>{project.progress}%</span>
        </div>
      </td>
      <td style={{ padding: '12px 16px', borderBottom: '1px solid #1a1a28' }}>
        {project.dueDate ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {overdue ? <AlertTriangle size={12} color="#ef4444" /> : <Clock size={12} color="#6b6880" />}
            <span style={{ fontSize: 12, color: overdue ? '#ef4444' : '#9ca3af' }}>{formatDate(project.dueDate)}</span>
          </div>
        ) : (
          <span style={{ color: '#4a4858', fontSize: 12 }}>—</span>
        )}
      </td>
      <td style={{ padding: '12px 16px', borderBottom: '1px solid #1a1a28' }}>
        <ChevronRight size={14} color="#4a4858" />
      </td>
    </tr>
  );
}

// ─── Main GestorPage ──────────────────────────────────────────────────────────

export default function GestorPage() {
  const navigate = useNavigate();
  const [view, setView] = useState<'grid' | 'list'>('list');
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<ProjectStatus | ''>('');
  const [filterDept, setFilterDept] = useState<Department | ''>('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth >= 768 && window.innerWidth < 1024);

  useEffect(() => {
    const handler = () => {
      const w = window.innerWidth;
      setIsMobile(w < 768);
      setIsTablet(w >= 768 && w < 1024);
      if (w < 768) setView('grid');
    };
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const { data: apiProjects, isLoading } = useQuery({
    queryKey: ['projects'],
    queryFn: () => projectsApi.list(),
    retry: false,
  });

  const _rawProjects = (apiProjects as { data: Project[]; total: number } | undefined)?.data ?? [];
  const projects: Project[] = _rawProjects.length > 0 ? _rawProjects : MOCK;

  const filtered = useMemo(() => {
    let list = [...projects];
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(p =>
        p.title.toLowerCase().includes(q) ||
        p.reference.toLowerCase().includes(q) ||
        p.client?.name.toLowerCase().includes(q) ||
        p.client?.company?.toLowerCase().includes(q)
      );
    }
    if (filterStatus) list = list.filter(p => p.status === filterStatus);
    if (filterDept) list = list.filter(p => p.steps.some(s => s.department === filterDept));
    return list;
  }, [projects, search, filterStatus, filterDept]);

  const selectStyle = {
    background: '#12121a',
    border: '1px solid #1e1e2e',
    borderRadius: 8,
    color: '#9ca3af',
    fontSize: 12,
    padding: '8px 12px',
    fontFamily: 'DM Sans, sans-serif',
    cursor: 'pointer',
    flex: isMobile ? '1 1 auto' : undefined,
  };

  const gridColumns = isMobile ? '1fr' : isTablet ? '1fr 1fr' : 'repeat(auto-fill, minmax(300px, 1fr))';

  return (
    <div style={{ padding: isMobile ? '20px 16px' : '32px', minHeight: '100vh', background: '#0a0a0f', fontFamily: 'DM Sans, sans-serif' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'flex-start' : 'flex-start',
        gap: isMobile ? 16 : 0,
        marginBottom: 28,
      }}>
        <div>
          <h1 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 900, fontSize: isMobile ? 24 : 28, color: '#fff', margin: '0 0 6px', letterSpacing: '-0.03em' }}>
            Proyectos
          </h1>
          <p style={{ fontSize: 13, color: '#6b6880', margin: 0 }}>
            {filtered.length} proyecto{filtered.length !== 1 ? 's' : ''} encontrado{filtered.length !== 1 ? 's' : ''}
          </p>
        </div>
        <button
          onClick={() => navigate('/gestor/nuevo')}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: 'linear-gradient(135deg, #f97316, #ea580c)',
            border: 'none', borderRadius: 10, padding: '10px 18px',
            color: '#fff', fontSize: 13, cursor: 'pointer',
            fontFamily: 'DM Sans, sans-serif', fontWeight: 600,
            boxShadow: '0 4px 14px rgba(249,115,22,0.35)',
            width: isMobile ? '100%' : 'auto',
            justifyContent: isMobile ? 'center' : undefined,
          }}
        >
          <Plus size={16} />
          Nuevo Proyecto
        </button>
      </div>

      {/* Filters bar */}
      <div style={{
        display: 'flex', gap: 10, marginBottom: 24,
        background: '#12121a', border: '1px solid #1e1e2e',
        borderRadius: 12, padding: '12px 16px',
        flexWrap: 'wrap', alignItems: 'center',
      }}>
        {/* Search */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '1 1 200px', background: '#0a0a0f', border: '1px solid #1e1e2e', borderRadius: 8, padding: '8px 12px' }}>
          <Search size={14} color="#6b6880" />
          <input
            type="text"
            placeholder="Buscar proyectos..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              background: 'transparent', border: 'none', outline: 'none',
              color: '#fff', fontSize: 13, width: '100%',
              fontFamily: 'DM Sans, sans-serif',
            }}
          />
        </div>

        {/* Status filter */}
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value as any)} style={selectStyle}>
          <option value="">Todos los estados</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>

        {/* Department filter */}
        <select value={filterDept} onChange={e => setFilterDept(e.target.value as any)} style={selectStyle}>
          <option value="">Todos los depts.</option>
          {DEPT_OPTIONS.map(d => (
            <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>
          ))}
        </select>

        {/* View toggle — hide on mobile (always grid) */}
        {!isMobile && (
          <div style={{ display: 'flex', background: '#0a0a0f', border: '1px solid #1e1e2e', borderRadius: 8, overflow: 'hidden', marginLeft: 'auto' }}>
            <button onClick={() => setView('list')} style={{ padding: '8px 12px', background: view === 'list' ? 'rgba(249,115,22,0.15)' : 'transparent', border: 'none', cursor: 'pointer', color: view === 'list' ? '#f97316' : '#6b6880', transition: 'all 0.15s' }}>
              <List size={15} />
            </button>
            <button onClick={() => setView('grid')} style={{ padding: '8px 12px', background: view === 'grid' ? 'rgba(249,115,22,0.15)' : 'transparent', border: 'none', cursor: 'pointer', color: view === 'grid' ? '#f97316' : '#6b6880', transition: 'all 0.15s' }}>
              <Grid3X3 size={15} />
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <div style={{ width: 32, height: 32, border: '3px solid #1e1e2e', borderTopColor: '#f97316', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        </div>
      ) : (isMobile || view === 'grid') ? (
        <div style={{ display: 'grid', gridTemplateColumns: gridColumns, gap: 16 }}>
          {filtered.map(p => (
            <ProjectGridCard key={p.id} project={p} onClick={() => navigate(`/gestor/proyecto/${p.id}`)} isMobile={isMobile} />
          ))}
          {filtered.length === 0 && (
            <div style={{ gridColumn: '1/-1', padding: '48px 16px', textAlign: 'center', color: '#4a4858', fontSize: 14 }}>
              No se encontraron proyectos
            </div>
          )}
        </div>
      ) : (
        <div style={{ background: '#12121a', border: '1px solid #1e1e2e', borderRadius: 14, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #1e1e2e' }}>
                {['Proyecto', 'Estado', 'Departamentos', 'Progreso', 'Fecha límite', ''].map(h => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, color: '#4a4858', fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.06em', fontWeight: 600 }}>
                    {h.toUpperCase()}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => (
                <ProjectTableRow key={p.id} project={p} onClick={() => navigate(`/gestor/proyecto/${p.id}`)} />
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ padding: '48px 16px', textAlign: 'center', color: '#4a4858', fontSize: 14 }}>
                    No se encontraron proyectos
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
